import React, { useCallback, useEffect, useState } from "react";
import { Typography } from "antd";
import { useFetchApi } from "../../../../../../hooks/useFetchApi";
import { ChartCard } from "../components/ChartCard";

const { Title } = Typography;

type FilterType = "quarterly" | "annually";

interface QuarterlyFinancialsProps {
  selectedStock?: any;
  baseCurrency: string;
}

export const QuarterlyFinancials: React.FC<QuarterlyFinancialsProps> = ({
  selectedStock,
  baseCurrency,
}) => {
  const [filter, setFilter] = useState<FilterType>("quarterly");

  const {
    execute: generateEPSGraph,
    loading: epsLoading,
    response: epsGraphData,
  } = useFetchApi("/api/dynamic-graph");

  const {
    execute: generateIncomeGraph,
    loading: incomeLoading,
    response: incomeGraphData,
  } = useFetchApi("/api/dynamic-graph");

  const {
    execute: generateAssetLinEquGraph,
    loading: assetLinEqLoading,
    response: assetLinEquGraphData,
  } = useFetchApi("/api/dynamic-graph");

  const fetchAllGraphs = useCallback(
    (activeFilter: FilterType) => {
      if (!selectedStock) return;

      const commonPayload = {
        selectedStocks: [selectedStock],
        filter: activeFilter,
        baseCurrency,
      };

      generateEPSGraph({
        ...commonPayload,
        selectedMetrics: [
          { name: "EPS Split Adjusted", sortableId: "1-Investor Ratio-5" },
        ],
        period: "5",
      });

      generateIncomeGraph({
        ...commonPayload,
        selectedMetrics: [
          { name: "revenues", sortableId: "7-Income Statement-1" },
          { name: "profit_before_tax", sortableId: "7-Income Statement-5" },
          { name: "net_profits", sortableId: "7-Income Statement-9" },
        ],
        period: "5",
      });

      generateAssetLinEquGraph({
        ...commonPayload,
        selectedMetrics: [
          { name: "total_assets", sortableId: "6-Balance Sheet-0" },
          { name: "total_liabilities", sortableId: "6-Balance Sheet-1" },
          { name: "total_equity", sortableId: "6-Balance Sheet-6" },
        ],
        period: "5",
      });
    },
    [selectedStock, baseCurrency],
  );

  useEffect(() => {
    fetchAllGraphs(filter);
  }, [selectedStock, baseCurrency, filter]);

  const filterLabel = filter === "quarterly" ? "Quarterly" : "Annual";

  return (
    <div>
      <Title level={5}>Quarterly Financials</Title>
      <div className="flex flex-col gap-6">
        <ChartCard
          title="Adj. EPS"
          subtitle={`Quarterly area trend`}
          graphData={epsGraphData}
          loading={epsLoading}
          baseCurrency={baseCurrency}
          defaultChartType="area"
          defaultFilter={filter}
          onFilterChange={setFilter}
          showFilterToggle={false}
        />

        <ChartCard
          title="Income"
          subtitle={`Quarterly area trend`}
          graphData={incomeGraphData}
          loading={incomeLoading}
          baseCurrency={baseCurrency}
          defaultChartType="area"
          defaultFilter={filter}
          onFilterChange={setFilter}
          showFilterToggle={false}
        />

        <ChartCard
          title="Assets, Liabilities & Equity"
          subtitle={`Quarterly area trend`}
          graphData={assetLinEquGraphData}
          loading={assetLinEqLoading}
          baseCurrency={baseCurrency}
          defaultChartType="area"
          defaultFilter={filter}
          onFilterChange={setFilter}
          showFilterToggle={false}
        />
      </div>
    </div>
  );
};
